import { Suspense, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { Switch } from 'react-router';
import { hot } from 'react-hot-loader';
import {
  businessCategorySelector,
  selectLanguage,
  selectTheme,
  shouldDisplayToolsBarSelector,
} from 'redux/selectors/config';
import { getAppTheme, GlobalStyles } from './globalStyles';
import TimeoutErrorAlert from './components/TimeoutErrorAlert';
import Header from './components/Header';
import Footer from './components/Footer';
import Helmet from './components/Helmet';
import Recaptcha from './components/Recaptcha';
import Routes from './Routes';
import AlertManager, {
  BannerAlert,
  SpecialAlert,
} from './components/AlertManager';
import { useApplication } from './hooks/application-hooks';
import { MainContentContainer } from 'components/MainContainer';
import PrivatePolicyModal from 'components/PrivatePoliciesModal';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { selectBannerAlertStatus } from 'redux/selectors/alerts';
import ApplicationLoader from './components/ApplicationLoader';
import ToolsBar from './components/Translator';
import ModalV2 from 'components/ModalV2';
import { BusinessCategory } from './constants/synergy';
import { Background } from 'components/MainContainer/MainContainerStyled';

const nsModulesByBusinessCategory = {
  [BusinessCategory.MOTOR]: ['synergy'],
  [BusinessCategory.HOME]: ['common', 'caseOpening'],
};

const App = () => {
  const ref = useRef(null);
  const theme = useSelector(selectTheme);
  const defaultLanguage = useSelector(selectLanguage);
  const displayBannerAlert = useSelector(selectBannerAlertStatus);
  const businessCategory = useSelector(businessCategorySelector);
  const shouldDisplayToolsBar = useSelector(shouldDisplayToolsBarSelector);
  const nsModules = nsModulesByBusinessCategory[businessCategory];

  useApplication();

  if (!theme) {
    return null;
  }

  const appTheme = getAppTheme({ theme });
  return (
    <Suspense fallback="">
      <I18nextProvider i18n={i18n(defaultLanguage, nsModules)}>
        <ThemeProvider theme={appTheme}>
          <Switch>
            <Background ref={ref}>
              <TimeoutErrorAlert />
              <Helmet />
              <GlobalStyles theme={appTheme} />
              <Header />
              <MainContentContainer>
                {shouldDisplayToolsBar && <ToolsBar data={{ ref }} />}
                {displayBannerAlert && <BannerAlert />}
                <SpecialAlert />
                <PrivatePolicyModal />
                <Routes />
                <Footer />
              </MainContentContainer>
              <ApplicationLoader />
              <AlertManager />
              <Recaptcha />
              <ModalV2 />
            </Background>
          </Switch>
        </ThemeProvider>
      </I18nextProvider>
    </Suspense>
  );
};

export const ConnectedApp = App;

export default hot(module)(ConnectedApp);
